import { AlertCircle, Info, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AdminCollectionStatus } from './types';

/**
 * `<StatusBadge>` — Phase 09 ADMIN-01 amenity-refresh status pill.
 * 5 variants per UI-SPEC § 3 table (idle / running / failed /
 * bringfido_skipped / done).
 *
 * Composes Phase 05 color tokens directly. Mirrors the role+aria+animate-
 * pulse discipline from Phase 07's <JobStatusBadge> but with a different
 * variant set — a separate component, not an extension.
 *
 * The `bringfido_skipped` variant is the slot #6 bronze allocation new in
 * Phase 09 — the bronze accent communicates "needs operator attention"
 * without the panic of coral-500's failed state.
 */
export type AdminStatusVariant =
  | 'idle'
  | 'running'
  | 'failed'
  | 'bringfido_skipped'
  | 'done';

/**
 * Map a server-emitted `AdminCollectionStatus` (county_coverage.collection_status,
 * which can be null) to a UI variant. `idle` is the default for "never refreshed".
 */
export function statusToVariant(status: AdminCollectionStatus): AdminStatusVariant {
  if (status === 'collected') return 'done';
  if (status === 'partial') return 'running';
  if (status === 'failed') return 'failed';
  if (status === 'bringfido_skipped') return 'bringfido_skipped';
  return 'idle';
}

const VARIANT_LABEL: Record<AdminStatusVariant, string> = {
  idle: 'Idle',
  running: 'Running',
  failed: 'Failed',
  bringfido_skipped: 'BringFido skipped',
  done: 'Done',
};

const VARIANT_CLASS: Record<AdminStatusVariant, string> = {
  idle: 'text-foam-50/60 bg-ocean-800/60 border-foam-50/20',
  running: 'text-moss-500 bg-moss-500/10 border-moss-500/40',
  failed: 'text-coral-500 bg-coral-500/10 border-coral-500/40',
  bringfido_skipped: 'text-bronze-500 bg-bronze-500/10 border-bronze-500/40',
  done: 'text-moss-700 bg-moss-700/10 border-moss-700/40',
};

const DEFAULT_BRINGFIDO_TOOLTIP =
  'BringFido lacks data for this county; manually populated subset';

export interface StatusBadgeProps {
  variant: AdminStatusVariant;
  /** Override the default tooltip text. Useful for failed cells with a gap_reason. */
  tooltip?: string;
  className?: string;
}

export function StatusBadge({ variant, tooltip, className }: StatusBadgeProps) {
  const label = VARIANT_LABEL[variant];
  const title =
    tooltip ?? (variant === 'bringfido_skipped' ? DEFAULT_BRINGFIDO_TOOLTIP : undefined);

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={`Status: ${label}`}
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 h-6 text-xs font-semibold whitespace-nowrap',
        VARIANT_CLASS[variant],
        className,
      )}
    >
      {variant === 'running' && (
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-1.5 rounded-full bg-moss-500 animate-pulse motion-reduce:animate-none motion-reduce:bg-moss-500/80"
        />
      )}
      {variant === 'failed' && <AlertCircle aria-hidden="true" className="w-3.5 h-3.5" />}
      {variant === 'bringfido_skipped' && <Info aria-hidden="true" className="w-3.5 h-3.5" />}
      {variant === 'done' && <Check aria-hidden="true" className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
}
